import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, FileText, Star, LifeBuoy, MapPin, UserRound, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { clientGet, errorMessage, isUnauthorized, fmtDate, fmtTime, fmtMoney } from '../../lib/clientApi';
import { RatingDialog } from './RatingDialog';
import { ContactAgencyButton } from './shared';

function Tile({ icon: Icon, title, children, testId }) {
  return (
    <Card className="p-4 shadow-sm border-border" data-testid={testId}>
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">{title}</h2>
          {children}
        </div>
      </div>
    </Card>
  );
}

export function ClientHome({ client, go, onUnauthorized }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await clientGet('/api/client/summary');
      setData(res.data);
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      setError(errorMessage(err, 'Impossible de charger votre espace'));
    } finally { setLoading(false); }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const next = data?.next_intervention;
  const unpaid = data?.unpaid_invoices;

  return (
    <div className="space-y-4" data-testid="client-home">
      <div>
        <h1 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight">Bonjour {client.name}</h1>
        {(client.address || client.town) && (
          <p className="text-base text-muted-foreground flex items-center gap-1 mt-1" data-testid="client-home-address"><MapPin className="h-4 w-4" /> {client.address}{client.address && ', '}{client.zip} {client.town}</p>
        )}
      </div>

      {loading && <div className="space-y-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-[var(--radius)]" />)}</div>}

      {!loading && error && (
        <Card className="p-4 border-destructive/30" role="alert" data-testid="client-home-error">
          <p className="text-base">{error}</p>
          <Button variant="outline" className="mt-3 h-11" onClick={load} data-testid="client-home-retry">Réessayer</Button>
        </Card>
      )}

      {!loading && data && (
        <>
          <Tile icon={CalendarDays} title="Prochaine intervention" testId="client-dashboard-next-intervention-card">
            {next ? (
              <div className="mt-1 text-base">
                <p className="font-medium capitalize" data-testid="client-next-intervention-date">{fmtDate(next.start)} {!next.fullday && `· ${fmtTime(next.start)}${next.end ? ` – ${fmtTime(next.end)}` : ''}`}</p>
                <p className="text-muted-foreground">{next.label}</p>
                {next.easer_name && <p className="text-muted-foreground flex items-center gap-1 mt-1"><UserRound className="h-4 w-4" /> Votre Easer : <span className="notranslate font-medium text-foreground">{next.easer_name}</span></p>}
              </div>
            ) : <p className="text-base text-muted-foreground mt-1">Aucune intervention prévue pour le moment.</p>}
            <Button variant="outline" className="mt-3 h-11" onClick={() => go('interventions')} data-testid="client-dashboard-interventions-link">Voir toutes mes interventions <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </Tile>

          <Tile icon={FileText} title="Factures à régler" testId="client-dashboard-unpaid-invoices-card">
            {unpaid?.count ? (
              <p className="text-base mt-1" data-testid="client-unpaid-summary">
                <span className="font-semibold">{unpaid.count} facture{unpaid.count > 1 ? 's' : ''}</span> pour un total de <span className="font-semibold">{fmtMoney(unpaid.total)}</span>
                {unpaid.overdue > 0 && <span className="text-destructive"> · {unpaid.overdue} en retard</span>}
              </p>
            ) : <p className="text-base text-muted-foreground mt-1">Vous êtes à jour, merci !</p>}
            <Button variant="outline" className="mt-3 h-11" onClick={() => go('factures')} data-testid="client-dashboard-invoices-link">Voir mes factures <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </Tile>

          <Tile icon={Star} title="Votre avis compte" testId="client-dashboard-pending-rating-card">
            {data.to_rate_count ? (
              <div className="mt-1 space-y-2">
                <p className="text-base text-muted-foreground">{data.to_rate_count} prestation{data.to_rate_count > 1 ? 's' : ''} attend{data.to_rate_count > 1 ? 'ent' : ''} votre évaluation.</p>
                {data.to_rate.map((ev) => (
                  <div key={ev.id} className="flex items-center justify-between gap-2 rounded-lg bg-secondary/60 px-3 py-2">
                    <div className="min-w-0 text-base">
                      <p className="font-medium truncate">{ev.label}</p>
                      <p className="text-muted-foreground text-sm capitalize">{fmtDate(ev.start, { day: 'numeric', month: 'long' })}{ev.easer_name && <> · <span className="notranslate">{ev.easer_name}</span></>}</p>
                    </div>
                    <Button size="sm" className="h-10" onClick={() => setRating(ev)} data-testid="client-dashboard-rate-button"><Star className="h-4 w-4 mr-1" /> Évaluer</Button>
                  </div>
                ))}
              </div>
            ) : <p className="text-base text-muted-foreground mt-1">Toutes vos prestations sont évaluées. Merci pour vos retours !</p>}
          </Tile>

          <Tile icon={LifeBuoy} title="Une question, une réclamation ?" testId="client-dashboard-tickets-card">
            <p className="text-base text-muted-foreground mt-1">{data.open_tickets ? `${data.open_tickets} demande${data.open_tickets > 1 ? 's' : ''} en cours de traitement.` : 'Déposez un ticket, l\'agence vous répond rapidement.'}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="outline" className="h-11" onClick={() => go('tickets')} data-testid="client-dashboard-tickets-link">Mes tickets <ArrowRight className="h-4 w-4 ml-2" /></Button>
              <ContactAgencyButton variant="ghost" testId="client-dashboard-contact-button" />
            </div>
          </Tile>
        </>
      )}

      <RatingDialog event={rating} onClose={() => setRating(null)} onDone={() => { setRating(null); toast.success('Merci pour votre avis !'); load(); }} onUnauthorized={onUnauthorized} />
    </div>
  );
}
